import RAPIER, {
  type Collider,
  type RigidBody,
  type RigidBodyDesc,
  type World,
} from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import {
  COIN_FINISH_PALETTES,
  COIN_VARIANT_IDS,
  DEFAULT_COIN_VARIANT,
  getCoinVariant,
} from './catalog';
import {
  NORMALIZED_COIN_RADIUS,
  NORMALIZED_COIN_THICKNESS,
  createCoinReliefGeometry,
  createSharedCoinBodyGeometry,
} from './geometry';
import { createCoinRandom, createCoinVariation } from './random';
import type {
  CoinCreateOptions,
  CoinFinish,
  CoinInstanceInfo,
  CoinInstanceSpec,
  CoinRuntimeMetadata,
  CoinStyleOptions,
  CoinTransformSource,
  CoinVariantId,
  CoinValue,
  DecorativeCoinPileOptions,
  PhysicsCoinHandle,
  PhysicsCoinOptions,
  QuaternionInput,
  Vector3Input,
} from './types';

interface CoinMaterialSet {
  readonly face: THREE.MeshStandardMaterial;
  readonly rim: THREE.MeshStandardMaterial;
  readonly relief: THREE.MeshStandardMaterial;
}

interface ResolvedCoinAssets {
  readonly metadata: CoinRuntimeMetadata;
  readonly bodyGeometry: THREE.BufferGeometry;
  readonly reliefGeometry: THREE.BufferGeometry;
  readonly materials: CoinMaterialSet;
}

interface ResolvedInstance {
  readonly info: CoinInstanceInfo;
  readonly assets: ResolvedCoinAssets;
  readonly position: THREE.Vector3;
  readonly quaternion: THREE.Quaternion;
  readonly uniformScale: number;
}

interface InstanceRecord {
  readonly info: CoinInstanceInfo;
  readonly body: THREE.InstancedMesh;
  readonly relief: THREE.InstancedMesh;
  readonly localIndex: number;
  readonly dimensions: THREE.Vector3;
  readonly mintRotation: number;
  uniformScale: number;
}

export interface CoinInstanceBatchOptions {
  readonly name?: string;
  readonly castShadow?: boolean;
  readonly receiveShadow?: boolean;
}

/** A two-mesh coin whose geometry and materials remain owned by its asset library. */
export class CoinObject extends THREE.Group {
  readonly body: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial[]>;
  readonly relief: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>;
  readonly metadata: CoinRuntimeMetadata;

  constructor(assets: ResolvedCoinAssets, options: CoinCreateOptions) {
    super();
    this.metadata = assets.metadata;
    this.name = options.name ?? `Coin ${assets.metadata.label} (${assets.metadata.finish})`;

    const visual = new THREE.Group();
    visual.name = 'CoinVisual';
    visual.scale.set(
      assets.metadata.dimensions.radius / NORMALIZED_COIN_RADIUS,
      assets.metadata.dimensions.thickness / NORMALIZED_COIN_THICKNESS,
      assets.metadata.dimensions.radius / NORMALIZED_COIN_RADIUS,
    );

    this.body = new THREE.Mesh(assets.bodyGeometry, [assets.materials.face, assets.materials.rim]);
    this.body.name = 'CoinBody';
    this.body.castShadow = options.castShadow ?? true;
    this.body.receiveShadow = options.receiveShadow ?? true;
    visual.add(this.body);

    this.relief = new THREE.Mesh(assets.reliefGeometry, assets.materials.relief);
    this.relief.name = 'CoinRelief';
    this.relief.rotation.y = assets.metadata.variation.mintRotation;
    this.relief.castShadow = options.castShadow ?? true;
    this.relief.receiveShadow = options.receiveShadow ?? true;
    visual.add(this.relief);

    this.add(visual);
    this.userData.coin = assets.metadata;
  }

  syncFromPhysics(source: CoinTransformSource): void {
    const translation = source.translation();
    const rotation = source.rotation();
    this.position.set(translation.x, translation.y, translation.z);
    this.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  }

  /** Detaches this coin. Shared GPU assets are released by CoinAssetLibrary.dispose(). */
  dispose(): void {
    this.removeFromParent();
    this.clear();
  }
}

export class PhysicsCoin implements PhysicsCoinHandle {
  private disposed = false;

  constructor(
    readonly object: CoinObject,
    readonly body: RigidBody,
    readonly collider: Collider,
    readonly world: World,
  ) {}

  get metadata(): CoinRuntimeMetadata {
    return this.object.metadata;
  }

  sync(): void {
    if (this.disposed) return;
    this.object.syncFromPhysics(this.body);
  }

  dispose(removeFromWorld = true): void {
    if (this.disposed) return;
    this.disposed = true;
    this.object.dispose();
    if (removeFromWorld && this.world.bodies.contains(this.body.handle)) {
      this.world.removeRigidBody(this.body);
    }
  }
}

/**
 * Multi-variant decorative batch. Each value/finish bucket costs three draw
 * calls: two material groups on the body and one relief, regardless of count.
 */
export class CoinInstanceBatch extends THREE.Group {
  readonly instances: readonly CoinInstanceInfo[];
  private readonly records: InstanceRecord[];
  private readonly scratchPosition = new THREE.Vector3();
  private readonly scratchQuaternion = new THREE.Quaternion();
  private readonly mintQuaternion = new THREE.Quaternion();
  private readonly scratchScale = new THREE.Vector3();
  private readonly scratchMatrix = new THREE.Matrix4();
  private readonly yAxis = new THREE.Vector3(0, 1, 0);
  private disposed = false;

  constructor(resolved: readonly ResolvedInstance[], options: CoinInstanceBatchOptions = {}) {
    super();
    this.name = options.name ?? 'Instanced Coins';

    const bucketSizes = new Map<string, number>();
    for (const entry of resolved) {
      const key = entry.assets.metadata.instancingKey;
      bucketSizes.set(key, (bucketSizes.get(key) ?? 0) + 1);
    }

    const buckets = new Map<
      string,
      { body: THREE.InstancedMesh; relief: THREE.InstancedMesh; nextIndex: number }
    >();
    for (const entry of resolved) {
      const key = entry.assets.metadata.instancingKey;
      if (buckets.has(key)) continue;
      const count = bucketSizes.get(key) ?? 0;
      const body = new THREE.InstancedMesh(entry.assets.bodyGeometry, [entry.assets.materials.face, entry.assets.materials.rim], count);
      body.name = `${key}: bodies`;
      body.castShadow = options.castShadow ?? true;
      body.receiveShadow = options.receiveShadow ?? true;
      body.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

      const relief = new THREE.InstancedMesh(entry.assets.reliefGeometry, entry.assets.materials.relief, count);
      relief.name = `${key}: reliefs`;
      relief.castShadow = options.castShadow ?? true;
      relief.receiveShadow = options.receiveShadow ?? true;
      relief.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.add(body, relief);
      buckets.set(key, { body, relief, nextIndex: 0 });
    }

    const records: InstanceRecord[] = [];
    const infos: CoinInstanceInfo[] = [];
    const tint = new THREE.Color();
    for (const entry of resolved) {
      const bucket = buckets.get(entry.assets.metadata.instancingKey);
      if (!bucket) throw new Error('Missing coin instance bucket.');
      const localIndex = bucket.nextIndex;
      bucket.nextIndex += 1;
      const dimensions = new THREE.Vector3(
        entry.assets.metadata.dimensions.radius / NORMALIZED_COIN_RADIUS,
        entry.assets.metadata.dimensions.thickness / NORMALIZED_COIN_THICKNESS,
        entry.assets.metadata.dimensions.radius / NORMALIZED_COIN_RADIUS,
      );
      const record: InstanceRecord = {
        info: entry.info,
        body: bucket.body,
        relief: bucket.relief,
        localIndex,
        dimensions,
        mintRotation: entry.assets.metadata.variation.mintRotation,
        uniformScale: entry.uniformScale,
      };
      records.push(record);
      infos.push(entry.info);
      this.writeTransform(record, entry.position, entry.quaternion, entry.uniformScale);
      tint.setRGB(
        entry.assets.metadata.variation.tint,
        entry.assets.metadata.variation.tint,
        entry.assets.metadata.variation.tint,
      );
      bucket.body.setColorAt(localIndex, tint);
      bucket.relief.setColorAt(localIndex, tint);
    }

    for (const { body, relief } of buckets.values()) {
      body.instanceMatrix.needsUpdate = true;
      relief.instanceMatrix.needsUpdate = true;
      if (body.instanceColor) body.instanceColor.needsUpdate = true;
      if (relief.instanceColor) relief.instanceColor.needsUpdate = true;
      body.computeBoundingSphere();
      relief.computeBoundingSphere();
    }
    this.records = records;
    this.instances = Object.freeze(infos);
  }

  setCoinTransform(
    index: number,
    position: Vector3Input,
    quaternion: QuaternionInput,
    uniformScale?: number,
  ): void {
    const record = this.records[index];
    if (!record) throw new RangeError(`Coin instance index ${index} is out of range.`);
    readVector3(position, this.scratchPosition);
    readQuaternion(quaternion, this.scratchQuaternion);
    record.uniformScale = uniformScale ?? record.uniformScale;
    this.writeTransform(record, this.scratchPosition, this.scratchQuaternion, record.uniformScale);
    record.body.instanceMatrix.needsUpdate = true;
    record.relief.instanceMatrix.needsUpdate = true;
  }

  refreshBounds(): void {
    const meshes = new Set<THREE.InstancedMesh>();
    for (const record of this.records) {
      meshes.add(record.body);
      meshes.add(record.relief);
    }
    for (const mesh of meshes) {
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeFromParent();
    for (const child of this.children) {
      if (child instanceof THREE.InstancedMesh) child.dispose();
    }
    this.clear();
  }

  private writeTransform(
    record: InstanceRecord,
    position: THREE.Vector3,
    quaternion: THREE.Quaternion,
    uniformScale: number,
  ): void {
    this.mintQuaternion.setFromAxisAngle(this.yAxis, record.mintRotation);
    this.scratchQuaternion.copy(quaternion).multiply(this.mintQuaternion);
    this.scratchScale.copy(record.dimensions).multiplyScalar(uniformScale);
    this.scratchMatrix.compose(position, this.scratchQuaternion, this.scratchScale);
    record.body.setMatrixAt(record.localIndex, this.scratchMatrix);
    record.relief.setMatrixAt(record.localIndex, this.scratchMatrix);
  }
}

export class CoinAssetLibrary {
  readonly bodyGeometry: THREE.BufferGeometry;
  private readonly reliefGeometries = new Map<CoinValue, THREE.BufferGeometry>();
  private readonly finishMaterials = new Map<CoinFinish, CoinMaterialSet>();
  private disposed = false;

  constructor(radialSegments = 48) {
    this.bodyGeometry = createSharedCoinBodyGeometry(radialSegments);
  }

  createMetadata(options: CoinStyleOptions & { readonly seed?: number | string } = {}): CoinRuntimeMetadata {
    this.assertActive();
    const definition = getCoinVariant(options.variant ?? DEFAULT_COIN_VARIANT);
    const finish = options.finish ?? definition.finish;
    const variation = createCoinVariation(options.seed ?? `${definition.id}:${finish}`);
    const dimensions = Object.freeze({
      radius: definition.radius,
      diameter: definition.radius * 2,
      thickness: definition.thickness,
      halfThickness: definition.thickness * 0.5,
    });
    return Object.freeze({
      kind: 'coin' as const,
      variant: definition.id,
      value: definition.value,
      label: definition.label,
      finish,
      rarity: definition.rarity,
      dimensions,
      collision: Object.freeze({
        shape: 'cylinder' as const,
        axis: 'y' as const,
        radius: definition.radius,
        halfHeight: definition.thickness * 0.5,
      }),
      orientation: Object.freeze({
        symmetryAxis: 'y' as const,
        obverseNormal: [0, 1, 0] as const,
        obverseUp: [0, 0, -1] as const,
        restingPlane: 'xz' as const,
      }),
      physics: Object.freeze({
        mass: definition.mass,
        friction: definition.friction,
        restitution: definition.restitution,
        linearDamping: definition.linearDamping,
        angularDamping: definition.angularDamping,
      }),
      instancingKey: `${definition.value}:${finish}`,
      variation,
    });
  }

  createCoin(options: CoinCreateOptions = {}): CoinObject {
    return new CoinObject(this.resolveAssets(options), options);
  }

  /**
   * Creates a Rapier body/collider and its visual. Call sync() once after each
   * fixed physics step. Rapier must already have been initialized by the game.
   */
  createPhysicsCoin(options: PhysicsCoinOptions): PhysicsCoin {
    const assets = this.resolveAssets(options);
    const object = new CoinObject(assets, options);
    const metadata = assets.metadata;
    const position = readVector3(
      options.position ?? [0, metadata.dimensions.halfThickness + 0.002, 0],
      new THREE.Vector3(),
    );
    const quaternion = readQuaternion(options.quaternion ?? [0, 0, 0, 1], new THREE.Quaternion());
    const descriptor = createRigidBodyDescriptor(options.bodyType ?? 'dynamic');
    descriptor
      .setTranslation(position.x, position.y, position.z)
      .setRotation(quaternion)
      .setLinearDamping(metadata.physics.linearDamping)
      .setAngularDamping(metadata.physics.angularDamping)
      .setCanSleep(true)
      .setCcdEnabled(options.ccd ?? false)
      .setUserData({
        kind: 'coin',
        variant: metadata.variant,
        value: metadata.value,
        finish: metadata.finish,
        seed: metadata.variation.seed,
      });

    if (options.linearVelocity) {
      const velocity = readVector3(options.linearVelocity, new THREE.Vector3());
      descriptor.setLinvel(velocity.x, velocity.y, velocity.z);
    }
    if (options.angularVelocity) {
      const velocity = readVector3(options.angularVelocity, new THREE.Vector3());
      descriptor.setAngvel(velocity);
    }

    const body = options.world.createRigidBody(descriptor);
    let colliderDescriptor = RAPIER.ColliderDesc.cylinder(
      metadata.collision.halfHeight,
      metadata.collision.radius,
    )
      .setMass(metadata.physics.mass)
      .setFriction(metadata.physics.friction)
      .setRestitution(metadata.physics.restitution);
    if (options.collisionGroups !== undefined) {
      colliderDescriptor = colliderDescriptor.setCollisionGroups(options.collisionGroups);
    }
    if (options.solverGroups !== undefined) {
      colliderDescriptor = colliderDescriptor.setSolverGroups(options.solverGroups);
    }
    const collider = options.world.createCollider(colliderDescriptor, body);
    const result = new PhysicsCoin(object, body, collider, options.world);
    result.sync();
    return result;
  }

  createInstancedCoins(
    entries: readonly CoinInstanceSpec[],
    options: CoinInstanceBatchOptions = {},
  ): CoinInstanceBatch {
    this.assertActive();
    const resolved = entries.map((entry, index): ResolvedInstance => {
      const assets = this.resolveAssets(entry);
      return {
        info: Object.freeze({ id: entry.id ?? `coin-${index}`, metadata: assets.metadata }),
        assets,
        position: readVector3(entry.position ?? [0, assets.metadata.dimensions.halfThickness, 0], new THREE.Vector3()),
        quaternion: readQuaternion(entry.quaternion ?? [0, 0, 0, 1], new THREE.Quaternion()),
        uniformScale: entry.uniformScale ?? 1,
      };
    });
    return new CoinInstanceBatch(resolved, options);
  }

  createDecorativePile(options: DecorativeCoinPileOptions): CoinInstanceBatch {
    this.assertActive();
    const count = Math.max(0, Math.floor(options.count));
    const pileRadius = Math.max(0.05, options.radius ?? 1.8);
    const maxLayers = Math.max(1, Math.floor(options.maxLayers ?? 5));
    const edgeCoinChance = THREE.MathUtils.clamp(options.edgeCoinChance ?? 0.08, 0, 0.35);
    const variants = options.variants?.length ? options.variants : COIN_VARIANT_IDS;
    const center = readVector3(options.center ?? [0, 0, 0], new THREE.Vector3());
    const random = createCoinRandom(options.seed ?? 0);
    const entries: CoinInstanceSpec[] = [];
    const euler = new THREE.Euler();
    const quaternion = new THREE.Quaternion();

    for (let index = 0; index < count; index += 1) {
      const variant = variants[Math.min(variants.length - 1, Math.floor(random() * variants.length))] as CoinVariantId;
      const definition = getCoinVariant(variant);
      const standing = random() < edgeCoinChance;
      const angle = random() * Math.PI * 2;
      const radial = standing
        ? pileRadius * (0.76 + random() * 0.22)
        : pileRadius * Math.sqrt(random());
      const centerWeight = 1 - radial / pileRadius;
      const availableLayers = Math.max(1, Math.ceil(1 + (maxLayers - 1) * centerWeight * centerWeight));
      const layer = Math.floor(random() * availableLayers);
      const yaw = random() * Math.PI * 2;
      if (standing) {
        euler.set(Math.PI * 0.5 + (random() - 0.5) * 0.18, yaw, (random() - 0.5) * 0.1, 'YXZ');
      } else {
        euler.set((random() - 0.5) * 0.16, yaw, (random() - 0.5) * 0.16, 'YXZ');
      }
      quaternion.setFromEuler(euler);
      entries.push({
        id: `pile-coin-${index}`,
        variant,
        seed: `${String(options.seed ?? 0)}:${index}`,
        position: [
          center.x + Math.cos(angle) * radial,
          center.y + (standing ? definition.radius * 0.92 : definition.thickness * (0.5 + layer * 0.82)),
          center.z + Math.sin(angle) * radial,
        ],
        quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
        uniformScale: 0.96 + random() * 0.08,
      });
    }

    return this.createInstancedCoins(entries, {
      name: 'Decorative Coin Pile',
      castShadow: options.castShadow,
      receiveShadow: options.receiveShadow,
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.bodyGeometry.dispose();
    for (const geometry of this.reliefGeometries.values()) geometry.dispose();
    this.reliefGeometries.clear();
    for (const materials of this.finishMaterials.values()) {
      materials.face.dispose();
      materials.rim.dispose();
      materials.relief.dispose();
    }
    this.finishMaterials.clear();
  }

  private resolveAssets(options: CoinStyleOptions & { readonly seed?: number | string }): ResolvedCoinAssets {
    const metadata = this.createMetadata(options);
    return {
      metadata,
      bodyGeometry: this.bodyGeometry,
      reliefGeometry: this.getReliefGeometry(metadata.value),
      materials: this.getFinishMaterials(metadata.finish),
    };
  }

  private getReliefGeometry(value: CoinValue): THREE.BufferGeometry {
    const cached = this.reliefGeometries.get(value);
    if (cached) return cached;
    const geometry = createCoinReliefGeometry(value);
    this.reliefGeometries.set(value, geometry);
    return geometry;
  }

  private getFinishMaterials(finish: CoinFinish): CoinMaterialSet {
    const cached = this.finishMaterials.get(finish);
    if (cached) return cached;
    const palette = COIN_FINISH_PALETTES[finish];
    const face = new THREE.MeshStandardMaterial({
      name: `${finish} coin face`,
      color: palette.face,
      metalness: 0.92,
      roughness: palette.roughness,
      envMapIntensity: 1.25,
      dithering: true,
    });
    const rim = new THREE.MeshStandardMaterial({
      name: `${finish} coin reeded rim`,
      color: palette.rim,
      metalness: 0.95,
      roughness: Math.min(0.42, palette.roughness + 0.1),
      envMapIntensity: 1.35,
      dithering: true,
    });
    const relief = new THREE.MeshStandardMaterial({
      name: `${finish} coin relief`,
      color: palette.relief,
      metalness: 0.9,
      roughness: Math.max(0.14, palette.roughness - 0.045),
      envMapIntensity: 1.45,
      dithering: true,
    });
    const result = { face, rim, relief };
    this.finishMaterials.set(finish, result);
    return result;
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('CoinAssetLibrary has been disposed.');
  }
}

export function createCoinAssetLibrary(radialSegments = 48): CoinAssetLibrary {
  return new CoinAssetLibrary(radialSegments);
}

export function syncCoinFromPhysics(object: THREE.Object3D, source: CoinTransformSource): void {
  const translation = source.translation();
  const rotation = source.rotation();
  object.position.set(translation.x, translation.y, translation.z);
  object.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
}

function createRigidBodyDescriptor(bodyType: PhysicsCoinOptions['bodyType']): RigidBodyDesc {
  switch (bodyType) {
    case 'kinematic-position':
      return RAPIER.RigidBodyDesc.kinematicPositionBased();
    case 'kinematic-velocity':
      return RAPIER.RigidBodyDesc.kinematicVelocityBased();
    case 'fixed':
      return RAPIER.RigidBodyDesc.fixed();
    default:
      return RAPIER.RigidBodyDesc.dynamic();
  }
}

function readVector3(value: Vector3Input, target: THREE.Vector3): THREE.Vector3 {
  if (Array.isArray(value)) return target.set(value[0], value[1], value[2]);
  const vector = value as Readonly<{ x: number; y: number; z: number }>;
  return target.set(vector.x, vector.y, vector.z);
}

function readQuaternion(value: QuaternionInput, target: THREE.Quaternion): THREE.Quaternion {
  if (Array.isArray(value)) return target.set(value[0], value[1], value[2], value[3]);
  const quaternion = value as Readonly<{ x: number; y: number; z: number; w: number }>;
  return target.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
}
